/**
 * Team → Daily update, Created today, Appraisal record, Workload; and a member's Done today and My record, which are
 * the same reads scoped to themselves (guide 5.5, 6.9; ADR-001 §7.6, §8.9). Schemas: team.ts. The appraisal record
 * also takes four writes (prototype incModal, attrModal, disModal, respModal): log, decide the cause, dismiss, add
 * one's side.
 */
import { IdempotencyHeaders, IfMatchHeaders, OptionalIfMatchHeaders } from './common.js';
import { API_BASE, defineRoute } from './route.js';
import { CreateIncidentBody, CreatedTodayResponse, DecideIncidentCauseBody, DismissIncidentBody, IncidentIdParams, IncidentResponseBody, RecordEntry, TeamDailyQuery, TeamDailyResponse, TeamRecordQuery, TeamRecordResponse, TeamWorkloadResponse, } from './team.js';
/** Managers (founder, associate director). */
const MANAGERS = ['team.read'];
/** Managers, and ops members for themselves. */
const MANAGERS_OR_SELF = ['team.read', 'today.own'];
const NOTE = 'Managers (founder, associate director). Others: 403.';
const ACT_HEADERS = IdempotencyHeaders.extend(OptionalIfMatchHeaders.shape);
const ACTION = { idempotency: 'key', concurrency: 'if-match-optional', rateLimit: 'write' };
const MGR_ACTION_NOTE = 'Managers (founder, associate director). The person it is about: 403. Anyone else: 404.';
const SELF_NOTE = 'Managers see everyone (or one person with user_id). An ops member sees only themselves: any other user_id is 403. ' +
    'Everyone else: 403.';
export const teamRoutes = {
    'team.daily': defineRoute({
        id: 'team.daily',
        method: 'GET',
        path: `${API_BASE}/team/daily`,
        summary: 'Daily update: what each person did on a working day, what moved, and what is still overdue',
        description: 'Built from activity_log (the domain services write it as people work), never typed up. Done = a task, ' +
            'routine, client task, escalation or request closed, or a meeting logged (`text` is the plain title, the kind ' +
            'labels it); other = dates moved, tasks left out or made. Made tasks are never one row each: a restored item ' +
            'is its own row, one meeting’s minutes are one row ("3 tasks from the minutes of …"), and every other task a ' +
            'person made that day is one row ("Added 10 tasks: 7 by hand, 3 from email"; the row itself when only one). ' +
            'A closed task that came from a checklist reads as a routine. `overdue` and `due_next_day` are as ' +
            'of now. `date` defaults to today (IST); a future date or one more than 90 days back is 422. For a member this ' +
            'is "Done today", themselves only. The "Preview the message" text is composed from this on the page.',
        access: { auth: 'session', anyOf: MANAGERS_OR_SELF, note: SELF_NOTE },
        query: TeamDailyQuery,
        success: { status: 200, description: 'The day, per person', schema: TeamDailyResponse },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'team.createdToday': defineRoute({
        id: 'team.createdToday',
        method: 'GET',
        path: `${API_BASE}/team/created-today`,
        summary: 'Created today: every task made today and where it came from, pending email proposals, and what was left out',
        description: 'Tasks created today (IST) by any source except the history import; every email proposal still pending (any ' +
            'day: it waits here until the SPOC confirms), `waiting` once pending PROPOSED_LATE_HOURS (4) or more; and items ' +
            'left out today. Newest first.',
        access: { auth: 'session', anyOf: MANAGERS, note: NOTE },
        success: { status: 200, description: 'Today’s new work', schema: CreatedTodayResponse },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'team.record': defineRoute({
        id: 'team.record',
        method: 'GET',
        path: `${API_BASE}/team/record`,
        summary: 'Appraisal record: per-person counts, one person’s entries, and what went right — facts, never a score',
        description: 'From incidents (escalations, missed deadlines, late responses, slipped dates, complaints, bad closures, ' +
            'mistakes; imported, noticed by the system or logged by a manager). An entry counts for a person when it was ' +
            'on their watch (owner) or a manager decided they caused it (cause person). `period` limits by occurred_on ' +
            '(and the went-right facts by their own dates); `kind` filters only the entries list. Dismissed entries are ' +
            'never counted; `dismissed=true` lists only them. Each entry carries its version, the person’s side, the ' +
            'dismissal and its trail. For a member this is "My record", themselves only.',
        access: { auth: 'session', anyOf: MANAGERS_OR_SELF, note: SELF_NOTE },
        query: TeamRecordQuery,
        success: { status: 200, description: 'The record', schema: TeamRecordResponse },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'incidents.get': defineRoute({
        id: 'incidents.get',
        method: 'GET',
        path: `${API_BASE}/incidents/{id}`,
        summary: 'One appraisal-record entry by id — the Review queue’s deep link, including an entry with no owner',
        description: 'The entry exactly as team.record lists it (relation as read in its owner’s record; on an entry with no owner, ' +
            'owner is null and relation is caused). This is how a manager opens an unreviewed entry that is on nobody’s ' +
            'record (no owner, cause not decided) to decide its cause: incidents.decideCause then needs caused_by_id for ' +
            '"our mistake". Managers read any entry; an ops member only an entry about themselves (their watch, or caused ' +
            'by them). Any other entry is 404, as is an id that does not exist, and so is any other role (an id is never confirmed).',
        access: {
            auth: 'session',
            anyOf: MANAGERS_OR_SELF,
            note: 'Managers: any entry. An ops member: only entries about themselves. Anything else, and every other role: 404.',
        },
        params: IncidentIdParams,
        success: { status: 200, description: 'The entry', schema: RecordEntry, headers: { ETag: 'W/"<version>"' } },
        errors: ['no_session', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'incidents.create': defineRoute({
        id: 'incidents.create',
        method: 'POST',
        path: `${API_BASE}/incidents`,
        summary: '"+ Log an incident" — an appraisal-record entry for something the system could not notice, or an old one',
        description: 'Written live (source live, raised_by the caller), incident_key manual:<id>. owner_id and caused_by_id must be ' +
            'active ops people, client_id a client that exists, occurred_on today or earlier (422 otherwise). cause person ' +
            'without a note is 422. An outcome records it closed. Recorded only in the appraisal record (an escalation ' +
            'kind does not open an escalation). Returns the entry as it reads in the owner’s record.',
        access: { auth: 'session', anyOf: MANAGERS, note: NOTE },
        headers: IdempotencyHeaders,
        body: CreateIncidentBody,
        success: { status: 201, description: 'Logged in the owner’s record', schema: RecordEntry },
        errors: ['no_session', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'incidents.decideCause': defineRoute({
        id: 'incidents.decideCause',
        method: 'PATCH',
        path: `${API_BASE}/incidents/{id}/cause`,
        summary: '"Review and decide the cause" — our mistake (whose), process gap, vendor, client side or unavoidable',
        description: 'Clears it from "Not reviewed". person: caused_by_id (an active ops person; default the owner) and a note — ' +
            'sent now or already there — are required (422). severity, note and outcome may be changed alongside; status ' +
            'is not. If-Match (the entry’s version) is required: 412 with the current entry when it changed. 409 once ' +
            'dismissed. Adds a trail line.',
        access: { auth: 'session', anyOf: MANAGERS_OR_SELF, action: MANAGERS, note: MGR_ACTION_NOTE },
        params: IncidentIdParams,
        headers: IdempotencyHeaders.extend(IfMatchHeaders.shape),
        body: DecideIncidentCauseBody,
        success: { status: 200, description: 'The entry', schema: RecordEntry, headers: { ETag: 'W/"<version>"' } },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        idempotency: 'key',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'incidents.dismiss': defineRoute({
        id: 'incidents.dismiss',
        method: 'POST',
        path: `${API_BASE}/incidents/{id}/dismiss`,
        summary: '"Dismiss" — kept in the record, marked dismissed with the reason, and no longer counted',
        description: 'Never deleted. 409 when already dismissed. Adds a trail line "Dismissed: <reason>".',
        access: { auth: 'session', anyOf: MANAGERS_OR_SELF, action: MANAGERS, note: MGR_ACTION_NOTE },
        params: IncidentIdParams,
        headers: ACT_HEADERS,
        body: DismissIncidentBody,
        success: { status: 200, description: 'The entry', schema: RecordEntry },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'incidents.respond': defineRoute({
        id: 'incidents.respond',
        method: 'POST',
        path: `${API_BASE}/incidents/{id}/response`,
        summary: '"Add my side" — the person it is about says what happened from their side and what they changed',
        description: 'Only the person the entry is about: its owner, or the person a manager decided caused it. Replaces their ' +
            'earlier text ("Edit my side"). 409 once dismissed. The Ops associate director sees it on the entry.',
        access: {
            auth: 'session',
            anyOf: MANAGERS_OR_SELF,
            note: 'The owner, or the person it was attributed to. A manager it is not about: 403. Anyone else: 404.',
        },
        params: IncidentIdParams,
        headers: ACT_HEADERS,
        body: IncidentResponseBody,
        success: { status: 200, description: 'The entry', schema: RecordEntry },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'team.workload': defineRoute({
        id: 'team.workload',
        method: 'GET',
        path: `${API_BASE}/team/workload`,
        summary: 'Workload: open, overdue, due today, done this week and load for each ops person',
        description: 'Live from open tasks (ADR §8.9 / guide 6.9): load = min(100, round(open / 8 × 100) + the person’s own ' +
            'capacity adjustment); red over 85, amber over 60. Hours planned come from today’s day plan; slips are due ' +
            'dates pushed in the last 30 days. The morning-digest preview and the per-person message are composed on the ' +
            'page from the person’s open tasks.',
        access: { auth: 'session', anyOf: MANAGERS, note: NOTE },
        success: { status: 200, description: 'Who has what', schema: TeamWorkloadResponse },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
};
//# sourceMappingURL=routes.team.js.map